/**
 * Reporting people and what they say, and what the operator does about it
 * (Terms, "Reporting, and what we do"). Anyone in a meeting can report
 * another person or a chat message, a saved chat can be reported after the
 * meeting, and a shared recording from its page. Reports go to the people who
 * run the service (by email, and to the Reports page of super admins), never
 * to the meeting's host or the person reported.
 */

export const REPORT_REASONS = ['harassment', 'hate', 'sexual', 'violence', 'self_harm', 'child_safety', 'illegal', 'spam', 'impersonation', 'other'] as const;
export type ReportReason = (typeof REPORT_REASONS)[number];

/** Each reason as the report form and the operator's email name it. */
export const REPORT_REASON_LABEL: Record<ReportReason, string> = {
  harassment: 'Harassment or bullying',
  hate: 'Hate speech',
  sexual: 'Sexual content',
  violence: 'Violence or threats',
  self_harm: 'Self-harm or suicide',
  child_safety: 'A child may be at risk',
  illegal: 'Something illegal',
  spam: 'Spam or a scam',
  impersonation: 'Pretending to be someone else',
  other: 'Something else',
};

/** Reports that are looked at first, and whose email says so. */
export const isUrgentReason = (reason: ReportReason): boolean => reason === 'child_safety';

export type ReportSource = 'meeting' | 'saved_chat' | 'shared_recording';
export type ReportOutcome = 'no_violation' | 'content_removed' | 'account_suspended' | 'duplicate';
export type ReportStatus = 'open' | 'closed';

/** What the person who reported is told in the email that closes their report. */
export const REPORT_OUTCOME_TEXT: Record<Exclude<ReportOutcome, 'duplicate'>, string> = {
  content_removed: 'We removed what you reported.',
  account_suspended: 'We suspended the account you reported.',
  no_violation: 'We looked into it and found nothing against our Terms. If it happens again, report it again.',
};

/** The longest "Anything else we should know?", in characters. */
export const REPORT_DETAILS_MAX = 1000;
/** Messages one report can point at in a meeting. */
export const REPORT_MESSAGE_IDS_MAX = 10;
/** Earlier messages by the same person kept with a report, so the operator sees what led up to it. */
export const REPORT_CONTEXT_MESSAGES = 20;
/** Reports about one person that one person can send in one meeting. */
export const REPORTS_PER_TARGET_PER_MEETING = 3;
/** Reports one person can send: in ten minutes, and in a day. */
export const REPORTS_PER_TEN_MINUTES = 5;
export const REPORTS_PER_DAY = 20;
/** How long a closed report is kept, in days, unless the law asks for longer. */
export const REPORT_KEPT_DAYS = 365;
/** The longest reason a super admin can give for suspending an account; it is sent to the account's owner. */
export const SUSPENSION_REASON_MAX = 500;
/** The longest note a super admin can keep with a report. Never sent to anyone. */
export const REPORT_NOTE_MAX = 2000;

// Control characters and the invisible ones that reorder text; line breaks stay.
const UNSAFE = /[\u0000-\u0009\u000b-\u001f\u007f-\u009f‪-‮⁦-⁩]/g;

/**
 * "Anything else we should know?" as it is kept: control characters out,
 * line breaks kept, cut to REPORT_DETAILS_MAX characters (never inside an
 * emoji). Empty when nothing is left.
 */
export function sanitizeReportDetails(input: unknown): string {
  if (typeof input !== 'string') return '';
  const text = input.normalize('NFC').replace(/\r\n?/g, '\n').replace(UNSAFE, '').trim();
  return Array.from(text).slice(0, REPORT_DETAILS_MAX).join('').trim();
}

/** One message kept with a report, as it was when the report was sent. Always one the reported person wrote. */
export interface ReportedMessage {
  id: string;
  /** ISO time it was sent. */
  at: string;
  text: string;
  /** The reporter pointed at this one; the others are kept for context. */
  reported: boolean;
}

/** A report as the list on the Reports page shows it. */
export interface ReportSummary {
  id: string;
  source: ReportSource;
  reason: ReportReason;
  /** A child may be at risk: listed first, and the email says [Urgent]. */
  urgent: boolean;
  /** The meeting's title, or the recording's. */
  title: string;
  roomCode: string | null;
  targetName: string;
  targetKind: 'account' | 'guest';
  reporterName: string;
  createdAt: string;
  status: ReportStatus;
  outcome: ReportOutcome | null;
  closedAt: string | null;
}

/** Everything the operator needs to decide about one report. Only super admins see it. */
export interface ReportDetail extends ReportSummary {
  details: string;
  messages: ReportedMessage[];
  reporter: { name: string; email: string | null; account: boolean };
  target: {
    name: string;
    email: string | null;
    /** The account reported; null for a guest, or once the account was deleted. */
    userId: string | null;
    role: 'host' | 'cohost' | 'participant' | 'guest' | null;
    suspendedAt: string | null;
    /** A super admin cannot be suspended. */
    superAdmin: boolean;
    /** The account pays for a plan with real money: suspending it does not stop that. */
    paysForPlan: boolean;
  };
  /** The account the meeting belongs to. */
  host: { name: string; email: string } | null;
  /** The other product that arranged the meeting, if one did. */
  integration: string | null;
  /** The recording reported, and whether it is still shared. */
  recording: { id: string; shared: boolean } | null;
  /** How many of the messages kept with the report are still in the meeting's saved chat. */
  messagesStillThere: number;
  note: string;
  closedByName: string | null;
}

/** An account a super admin suspended. */
export interface SuspendedAccount {
  id: string;
  name: string;
  email: string;
  suspendedAt: string;
  /** What the owner was told. */
  reason: string;
  /** The super admin who did it; null once their account is gone. */
  byName: string | null;
}
